/* De lijst met opvolgpunten, voor het dashboard (mijn punten, per project) en voor de Projectpagina
 * (alles van dit project, ook van collega's). Alleen de eigenaar kan afvinken, laten vervallen en
 * koppelen; een collega leest mee.
 *
 * Op het dashboard staan de punten onder een kop per project: nummer, naam, de stip van het stadium
 * en de contactpersonen. Elke rij heeft één metaregel (hoe laat, hoe lang stil, wanneer verstuurd)
 * en zijn acties rechts. Een rand links zegt hoe dringend het is: rood is te laat, oranje is vandaag.
 *
 * Een punt dat op een reactie wacht toont zijn klok (ADR 0030). "Gebeld" legt alleen vast dat de
 * eigenaar dat zelf heeft gedaan; hier wordt niets verstuurd. "Hoort hier niet" zit achter een menu
 * en is een halve minuut ongedaan te maken, zodat een misklik niet stil een punt laat verdwijnen. */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MoreHorizontal, Phone, PhoneForwarded, Undo2, X } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ToastAction } from '@/components/ui/toast';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useProjectenKort } from '@/features/projectlijst/hooks';
import {
  actieTekst, dagenTot, lokaleDag, perProject, puntDag, SOORT_LABEL, stilTekst, stukLabel, termijnTekst, verstuurdDag,
  type Opvolgpunt, type ProjectKop,
} from '../data/opvolging';
import { useOpvolgActies } from '../hooks';

/** Dezelfde stip als de projectenlijst en het dashboard. */
const STIP: Record<string, string> = { verkennen: 'bg-blue-600', voorstel: 'bg-purple-600', gewonnen: 'bg-green-600', operatie: 'bg-emerald-700' };

function KiesProject({ punt, label }: { punt: Opvolgpunt; label: string }) {
  const [open, setOpen] = useState(false);
  const kort = useProjectenKort({ enabled: open });
  const { koppel } = useOpvolgActies();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={koppel.isPending} className="rounded underline decoration-dotted underline-offset-2 hover:text-foreground">
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[340px] p-0">
        <Command>
          <CommandInput placeholder="Zoek op nummer of naam…" />
          <CommandList>
            <CommandEmpty>Geen project gevonden.</CommandEmpty>
            <CommandGroup>
              {(kort.data ?? []).map((p) => (
                <CommandItem
                  key={p.id}
                  value={`${p.project_number ?? ''} ${p.name}`}
                  onSelect={() => {
                    setOpen(false);
                    koppel.mutate({ punt, projectId: p.id });
                  }}
                  className="gap-2"
                >
                  <span className="w-10 shrink-0 font-mono text-xs text-muted-foreground">{p.project_number ?? '—'}</span>
                  <span className="flex-1 truncate">{p.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Een klein rond knopje rechts in de rij: bellen, de mail openen, het menu. */
const KNOP = 'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50';

function Rij({ punt, mijnId, contactInKop, metProject }: {
  punt: Opvolgpunt;
  mijnId: string | null;
  /** Het contact staat al in de projectkop; dan niet nog eens in de rij. */
  contactInKop: boolean;
  /** Op het dashboard: een punt zonder project krijgt "Kies project". */
  metProject: boolean;
}) {
  const { status, opgevolgd } = useOpvolgActies();
  const { toast } = useToast();
  const vanMij = punt.eigenaar_id === mijnId;
  const dicht = punt.status !== 'open';
  const wacht = punt.soort === 'wacht_op_antwoord';
  const dagen = dagenTot(puntDag(punt));
  const contact = punt.contact_naam || punt.contact_email;
  const stuk = stukLabel(punt);
  const stil = wacht && !dicht ? stilTekst(punt.klok_start) : null;
  const verstuurd = wacht ? verstuurdDag(punt) : null;
  const vandaagOpgevolgd = punt.opgevolgd_op === lokaleDag(new Date());
  const teLaat = !dicht && dagen !== null && dagen < 0;
  const vandaag = !dicht && dagen === 0;

  function laatVervallen() {
    status.mutate({ id: punt.id, status: 'vervallen' }, {
      onSuccess: () => toast({
        title: 'Punt vervallen',
        description: punt.tekst,
        action: <ToastAction altText="Zet het punt terug" onClick={() => status.mutate({ id: punt.id, status: 'open' })}>Ongedaan maken</ToastAction>,
      }),
    });
  }

  return (
    <li className={cn('flex items-start gap-3 border-l-[3px] py-2.5 pl-3 pr-3 text-sm', teLaat ? 'border-l-red-500' : vandaag ? 'border-l-amber-500' : 'border-l-transparent')}>
      {vanMij ? (
        <Checkbox
          id={`op-${punt.id}`}
          className="mt-0.5"
          checked={dicht}
          disabled={status.isPending}
          onCheckedChange={() => status.mutate({ id: punt.id, status: dicht ? 'open' : 'afgehandeld' })}
          aria-label={`${punt.tekst} ${dicht ? 'weer openzetten' : 'afhandelen'}`}
        />
      ) : (
        <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', dicht ? 'bg-muted-foreground/40' : 'bg-amber-500')} aria-hidden />
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <label
          htmlFor={vanMij ? `op-${punt.id}` : undefined}
          className={cn('break-words leading-snug', vanMij && 'cursor-pointer', dicht && 'text-muted-foreground line-through')}
          title={stuk ? [punt.tekst, punt.stuk_bestand].filter(Boolean).join(' · ') : undefined}
        >
          {stuk ? <><span className="font-medium">{stuk}</span>{contact ? ` aan ${contact}` : ''}</> : punt.tekst}
        </label>
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
          {!dicht && (
            <span className={cn(teLaat && 'font-medium text-red-700', vandaag && 'font-medium text-amber-700')}>
              {wacht ? actieTekst(punt) : termijnTekst(punt.termijn)}
            </span>
          )}
          {stil && <><span aria-hidden>·</span><span>{stil}</span></>}
          {verstuurd && !stil?.startsWith('vandaag') && <><span aria-hidden>·</span><span>verstuurd {verstuurd}</span></>}
          {/* Met een klok of een stuk zegt de regel zelf al wat het is. */}
          {punt.soort !== 'toezegging' && !wacht && <><span aria-hidden>·</span><span>{SOORT_LABEL[punt.soort]}</span></>}
          {contact && !stuk && !contactInKop && <><span aria-hidden>·</span><span className="truncate">{punt.soort === 'open_vraag' ? 'van' : 'aan'} {contact}</span></>}
          {!vanMij && punt.eigenaar_naam && <><span aria-hidden>·</span><span>door {punt.eigenaar_naam}</span></>}
          {metProject && vanMij && !punt.project_id && <><span aria-hidden>·</span><KiesProject punt={punt} label="Kies project" /></>}
          {/* Vanzelf gekoppeld kan mis zijn; de eigenaar zet het in één klik recht. */}
          {metProject && vanMij && punt.project_id && punt.koppeling === 'vanzelf' && <><span aria-hidden>·</span><KiesProject punt={punt} label="ander project" /></>}
        </span>
      </div>

      {!dicht && (
        <div className="flex shrink-0 items-center gap-1">
          {vanMij && wacht && (
            <button
              type="button"
              disabled={opgevolgd.isPending}
              onClick={() => opgevolgd.mutate({ id: punt.id, ongedaan: vandaagOpgevolgd })}
              className={cn(KNOP, 'w-auto gap-1 px-2 text-xs')}
              title={vandaagOpgevolgd
                ? 'Toch niet: zet de klok terug'
                : 'Ik heb zelf gebeld of gemaild: herinner me over een week weer. Hier wordt niets verstuurd.'}
            >
              {vandaagOpgevolgd ? <Undo2 className="h-3.5 w-3.5" aria-hidden /> : <PhoneForwarded className="h-3.5 w-3.5" aria-hidden />}
              {vandaagOpgevolgd ? 'Ongedaan' : 'Gebeld'}
            </button>
          )}
          {punt.telefoon && (
            <a href={`tel:${punt.telefoon.replace(/[^\d+]/g, '')}`} className={KNOP} title={`Bellen: ${punt.telefoon}`} aria-label={`Bellen: ${punt.telefoon}`}>
              <Phone className="h-3.5 w-3.5" aria-hidden />
            </a>
          )}
          {vanMij && punt.mail_url && (
            <a href={punt.mail_url} target="_blank" rel="noreferrer" className={KNOP} title={punt.onderwerp ? `Mail: ${punt.onderwerp}` : 'Mail openen'} aria-label="Mail openen">
              <Mail className="h-3.5 w-3.5" aria-hidden />
            </a>
          )}
          {vanMij && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={KNOP} aria-label={`${punt.tekst}: meer`}>
                  <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled={status.isPending} onSelect={laatVervallen} className="gap-2">
                  <X className="h-3.5 w-3.5" aria-hidden />
                  Hoort hier niet
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
    </li>
  );
}

/** De unieke contacten van een groep, voor in de kop: "Max Kapper", of "Max Kapper, Kayleigh Keunen". */
function contactenVan(punten: Opvolgpunt[]): string[] {
  return [...new Set(punten.map((p) => p.contact_naam || p.contact_email).filter((c): c is string => Boolean(c)))];
}

function ProjectKopRij({ projectId, kop, punten }: { projectId: string | null; kop: ProjectKop | undefined; punten: Opvolgpunt[] }) {
  const contacten = contactenVan(punten);
  const contactTekst = contacten.length > 2 ? `${contacten.slice(0, 2).join(', ')} +${contacten.length - 2}` : contacten.join(', ');
  const aantal = `${punten.length} ${punten.length === 1 ? 'punt' : 'punten'}`;
  return (
    <div className="flex items-center gap-2 border-y bg-muted/40 px-4 py-1.5 text-xs first:border-t-0">
      <span className={cn('h-2 w-2 shrink-0 rounded-full', projectId ? STIP[kop?.stage ?? ''] ?? 'bg-slate-400' : 'bg-slate-300')} aria-hidden />
      {projectId ? (
        <Link to={`/crm/projects/${projectId}`} className="flex min-w-0 items-baseline gap-2 hover:underline">
          {kop?.nummer && <span className="shrink-0 font-mono text-muted-foreground">{kop.nummer}</span>}
          <span className="truncate font-medium text-foreground">{kop?.naam ?? 'Project'}</span>
        </Link>
      ) : (
        <span className="font-medium text-muted-foreground">Zonder project</span>
      )}
      {contactTekst && <span className="truncate text-muted-foreground">· {contactTekst}</span>}
      <span className="ml-auto shrink-0 text-muted-foreground">{aantal}</span>
    </div>
  );
}

export function OpvolgLijst({ punten, mijnId, project }: {
  punten: Opvolgpunt[];
  mijnId: string | null;
  /** Geef hem mee waar de punten per project horen te staan (dashboard); laat hem weg op de Projectpagina. */
  project?: (id: string) => ProjectKop | undefined;
}) {
  if (!project) {
    return (
      <ul className="divide-y">
        {punten.map((p) => <Rij key={p.id} punt={p} mijnId={mijnId} contactInKop={false} metProject={false} />)}
      </ul>
    );
  }
  return (
    <div>
      {perProject(punten).map((g) => {
        const contactInKop = contactenVan(g.punten).length === 1;
        return (
          <section key={g.projectId ?? 'zonder'} aria-label={g.projectId ? project(g.projectId)?.naam ?? 'Project' : 'Zonder project'}>
            <ProjectKopRij projectId={g.projectId} kop={g.projectId ? project(g.projectId) : undefined} punten={g.punten} />
            <ul className="divide-y">
              {g.punten.map((p) => <Rij key={p.id} punt={p} mijnId={mijnId} contactInKop={contactInKop} metProject />)}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
